import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Sidebar, SidebarItem } from '@insights_module/components/Sidebar';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { ContextualTaxonomy } from '@insights_module/types/ContextualAndEntity';
import { Spacer } from '@components/Spacer';
import { useShowDetailsContext } from '@insights_module/context/ShowDetailsContext';
import { ContextualTierBadge } from '@insights_module/components/ContextualTierBadge';
import { useShowDetailsTableData } from '@insights_module/hooks/useShowDetailsTableData';
import { AudioContextualSummary } from '@insights_module/types/graphql/Schema';

export const OpenCollapsedButton = tw.div`flex items-center justify-center bg-special rounded text-[11px] font-medium cursor-pointer px-2 py-[5px]`;
const Labels = tw.div`flex justify-between items-center w-full min-h-[33px] text-text-and-icon-muted text-[11px] font-medium bg-gray-2 rounded px-[6px]`;
const OverviewOuterWrapper = tw.div`flex flex-col bg-transparent w-full h-[calc(100%-33px)] relative`;
const NoResultsWrapper = tw.div`flex h-full bg-gray-2 p-3 text-[11px] rounded-lg text-text-and-icon`;
const StyledSubtitle = tw.span`text-text-and-icon-muted text-[11px] leading-normal block tracking-wide`;
const StyledListItem = tw.div`flex items-center justify-between px-[6px] py-[10px] text-[11px] font-medium h-[33px]`;
const ListItemInnerWrapper = tw.div`flex gap-[8px] items-end`;

const CategoryListItem = ({ category }: { category: AudioContextualSummary }) => {
  const { selectedShowDetailsCategory, setSelectedShowDetailsCategory } = useShowDetailsContext();

  return (
    <StyledListItem>
      <ListItemInnerWrapper>
        <ContextualTierBadge tier={category.taxonomy?.[0]?.tier} />
        <button
          tw='underline h-[20px] align-middle text-text-and-icon'
          css={[selectedShowDetailsCategory?.name === category.name && tw`text-accent-1`]}
          onClick={() => setSelectedShowDetailsCategory(category)}>
          {category.display_name}
        </button>
      </ListItemInnerWrapper>
      <span tw='text-text-and-icon-muted'>{category.audio_count}</span>
    </StyledListItem>
  );
};

const NoResults = ({ taxonomy }: { taxonomy: ContextualTaxonomy }) => {
  const { t } = useTranslation();

  return (
    <NoResultsWrapper>{t(`insights.categories_overview.no_results.${taxonomy}`)}</NoResultsWrapper>
  );
};

const CategoryList = ({
  categories,
  taxonomy,
}: {
  categories: AudioContextualSummary[];
  taxonomy: ContextualTaxonomy;
}) => {
  const { t } = useTranslation();
  if (categories.length === 0) {
    return <NoResults taxonomy={taxonomy} />;
  }

  return (
    <>
      <Spacer direction='vertical' size='xxs' />
      <StyledSubtitle>{t('insights.categories_overview.sub_title')}</StyledSubtitle>
      <Spacer direction='vertical' size='sm' />
      <Labels>
        <span>{t('insights.categories_overview.labels.category')} </span>
        <span>{t('insights.categories_overview.labels.num_of_episodes')} </span>
      </Labels>
      <div tw='flex flex-col h-full overflow-scroll'>
        {categories.map((category) => (
          <CategoryListItem key={category.id} category={category} />
        ))}
      </div>
    </>
  );
};

const CategoriesOverview = () => {
  const { t } = useTranslation();
  const { selectedContextualCategory } = useInsightsContext();
  const {
    contextual: { iabData, appleData },
    isLoading,
  } = useShowDetailsTableData();

  return (
    <SidebarItem
      isLoading={isLoading}
      hasDivider={true}
      header={<Sidebar.Title>{t('insights.categories_overview.title')}</Sidebar.Title>}>
      <OverviewOuterWrapper>
        {selectedContextualCategory === 'iab' && (
          <CategoryList categories={iabData} taxonomy={'iab'} />
        )}
        {selectedContextualCategory === 'apple' && (
          <CategoryList categories={appleData} taxonomy={'apple'} />
        )}
      </OverviewOuterWrapper>
    </SidebarItem>
  );
};

export { CategoriesOverview };
